import React from 'react';
import I18n from 'i18n-js';
import { useAdyenCard } from './adyen_card_hook';
import { AdyenCardType } from '../lib/adyen';
import {
  core_apimessages_AddressEntry as AddressEntry,
  core_apimessages_Address as Address,
  core_apimessages_Country as Country,
  core_apimessages_CreditCardTokenizedFields as CreditCardTokenizedFields,
  core_apimessages_Error as Error,
} from '../schemas/rql';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';

import { RCCheckbox } from '@reverbdotcom/commons/src/rc/forms';
import CreditCardEncryptedInput from './credit_card_encrypted_input';
import CreditCardEncryptedDateInput from './credit_card_encrypted_date_input';
import AddressFormFields from './address_form_fields';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';
import AddressSummary from './address_summary';
import { noop } from 'lodash';

interface CreditCardEntry {
  cardholderName: string;
  tokenizedFields: CreditCardTokenizedFields;
  brandType: string;
  lastDigits: string;
}

export interface CreateCreditCardFormInput {
  creditCard: CreditCardEntry;
  billingAddress: AddressEntry;
}

type OnCreditCardSubmit = (input: CreateCreditCardFormInput) => any;

interface Props {
  formId: string;
  defaultBillingAddress: Address;
  countries: Country[];
  onSubmit: OnCreditCardSubmit;
  errors: Error[];
  onCardValidChange?: (valid: boolean) => void;
}

const CREDIT_CARD_TROUBLESHOOTING_TIPS_ARTICLE_ID = '1500005385482';

export default function CreditCardCreateForm(props: Props) {
  const isDefaultBillingAddressComplete = props.defaultBillingAddress.isComplete;
  const [isBillingSameAsShipping, setBillingSameAsShipping] = React.useState(isDefaultBillingAddressComplete);
  const [billingAddress, setBillingAddress] = React.useState<AddressEntry>({ ...emptyAddressEntry, ...props.defaultBillingAddress });
  const onCardValidChange = props.onCardValidChange || noop;

  const { cardState, focus, errors: adyenErrors, brand, endDigits } = useAdyenCard({
    selector: '[data-card-fields]',
    cardType: AdyenCardType.Card,
  });

  const { paymentMethod } = cardState.data;
  const { isValid } = cardState;

  React.useEffect(() => {
    onCardValidChange(isValid);
  }, [isValid, onCardValidChange]);

  function onToggleBillingSameAsShipping() {
    if (isBillingSameAsShipping) {
      setBillingAddress(emptyAddressEntry);
    } else {
      setBillingAddress(props.defaultBillingAddress);
    }

    setBillingSameAsShipping(previous => !previous);
  }

  async function onSubmit(e: React.ChangeEvent<HTMLFormElement>) {
    e.preventDefault();

    props.onSubmit({
      creditCard: {
        cardholderName: billingAddress.name,
        tokenizedFields: {
          encryptedExpiryMonth: paymentMethod.encryptedExpiryMonth,
          encryptedExpiryYear: paymentMethod.encryptedExpiryYear,
          encryptedCardNumber: paymentMethod.encryptedCardNumber,
          encryptedSecurityCode: paymentMethod.encryptedSecurityCode,
        },
        brandType: brand,
        lastDigits: endDigits,
      },
      billingAddress: {
        name: billingAddress.name,
        streetAddress: billingAddress.streetAddress,
        extendedAddress: billingAddress.extendedAddress,
        postalCode: billingAddress.postalCode,
        phone: billingAddress.phone,
        region: billingAddress.region,
        locality: billingAddress.locality,
        countryCode: billingAddress.countryCode,
      },
    });
  }

  const troubleshootingUrl = useHelpCenterArticleUrl(CREDIT_CARD_TROUBLESHOOTING_TIPS_ARTICLE_ID);

  return (
    <form id={props.formId} onSubmit={onSubmit}>
      <div data-card-fields className="g-container">
        {props.errors.length > 0 && (
          <div className="mb-1">
            <RCAlertBox type="error">
              {props.errors.map(e => <li key={`${e.field}-${e.message}`}>{e.message}</li>)}
              <div className="mt-1">
                <a href={troubleshootingUrl}>
                  {I18n.t('discovery.creditCardWallet.cardTroubleshooting')}
                </a>
              </div>
            </RCAlertBox>
          </div>
        )}
        <div className="g-col-12 scaling-mb-2">
          <CreditCardEncryptedInput
            inputName="cardNumber"
            inputValue={paymentMethod.encryptedCardNumber}
            cseName="encryptedCardNumber"
            label={
              <span className="icon-r-lock">
                {I18n.t('discovery.creditCardWallet.createForm.cardNumber')}
              </span>
            }
            errors={adyenErrors}
            focus={focus}
            brand={brand}
            showBrandIcon
          />
        </div>
        <div className="g-col-5 g-col-mobile-12 scaling-mb-2">
          <CreditCardEncryptedDateInput
            expiryMonthInputName="expiryMonth"
            expiryYearInputName="expiryYear"
            paymentMethod={paymentMethod}
            label={I18n.t('discovery.creditCardWallet.createForm.expiration')}
            errors={adyenErrors}
            focus={focus}
          />
        </div>
        <div className="g-col-7 g-col-mobile-12 scaling-mb-2">
          <CreditCardEncryptedInput
            inputName="securityCode"
            inputValue={paymentMethod.encryptedSecurityCode}
            label={
              <span className="icon-r-lock">
                {I18n.t('discovery.creditCardWallet.createForm.securityCode')}
              </span>
            }
            cseName="encryptedSecurityCode"
            errors={adyenErrors}
            focus={focus}
            brand={brand}
            showSecurityCodeIcon
          />
        </div>
      </div>
      {isDefaultBillingAddressComplete &&
        <div className="d-flex fx-dir-col">
          <div className="mt-1">
            <RCCheckbox
              id="credit-card-create-billing"
              name="credit-card-create-billing"
              label={I18n.t('discovery.creditCardWallet.createForm.billingSame')}
              checked={isBillingSameAsShipping}
              onChange={onToggleBillingSameAsShipping}
            />
          </div>
        </div>
      }
      <div className="bdt-1 bd-color-primary mtb-2" />
      <BillingAddress
        showEdit={isBillingSameAsShipping}
        billingAddress={billingAddress}
        onAddressUpdate={setBillingAddress}
        countries={props.countries}
      />
    </form>
  );
}

function BillingAddress(props: {
  showEdit: boolean,
  billingAddress: Address,
  countries: Country[],
  onAddressUpdate: (address: Address) => void,
}) {
  const { billingAddress } = props;

  if (props.showEdit) {
    return (
      <div className="mt-half size-90">
        <AddressSummary address={billingAddress} />
      </div>
    );
  }

  return (
    <AddressFormFields
      address={billingAddress}
      countries={props.countries}
      updateField={props.onAddressUpdate}
      requiredFieldsOverrides={['phone']}
      useCamelizedFieldsForRQL
    />
  );
}

const emptyAddressEntry = {
  countryCode: '',
  name: '',
  streetAddress: '',
  extendedAddress: '',
  locality: '',
  region: '',
  postalCode: '',
  phone: '',
};
